import type { DashboardTab } from '../DashboardView'
import { PINNED_PAYROLL_SOURCE } from '../payrollSource'

// With the payroll source pinned (switch retired), the cash charts read only
// that source, so the map draws only its path into them: GOSPEL pinned, the
// payroll tab still feeds Breakeven and the export; payroll tab pinned (or the
// kill switch thrown, lock or not), the GOSPEL sync reaches no chart. With the
// per-viewer switch back (nothing pinned), both paths are drawn as before.
const GOSPEL_ONLY = PINNED_PAYROLL_SOURCE === 'gospel'
const PAYROLL_TAB_ONLY = PINNED_PAYROLL_SOURCE === 'payroll-tab'

// The "where these numbers come from" pipeline, as data. Three columns: where a
// figure lives (source), how it gets here (job), where you see it (section).
// Adding a source or a section means adding nodes and edges below, nothing else.
//
// The edges were derived by hand from the SQL views that feed each section
// (gospel_cash_flow, cash_position_by_month, software_pl_by_month,
// balance_reconciliation, pipeline_overlap_report, headcount_plan_reconciliation)
// and from the export's tab list (export.repository.ts). If a view's inputs
// change, this file changes with it. test/sourceMap.graph.spec.ts checks the
// parts that can be checked: the charts' own source map (CHART_SOURCES), the job
// labels, the dashboard anchors and the sheet ids in terraform.tfvars.
//
// Nodes appear top to bottom in the order that keeps edge crossings low.

export type MapColumn = 'source' | 'job' | 'section'
export type SectionTarget = { tab: DashboardTab; anchor: string | null }
export type NodeLink =
  | { kind: 'external'; url: string } // opens in a new tab
  | { kind: 'pending' } // a link exists in the world, just isn't added here yet
  | { kind: 'none' } // nothing to link to, by design
  | { kind: 'section'; target: SectionTarget }
export type MapNode = {
  id: string
  column: MapColumn
  title: string
  detail: string
  link: NodeLink
  job?: string // job nodes only: the sourceFreshness `job` id, when tracked
  cadence?: string // job nodes only
  output?: boolean // section column only: leaves the dashboard (the export sheet)
}
export type MapEdge = { from: string; to: string }
export type SourceGraph = { nodes: readonly MapNode[]; edges: readonly MapEdge[] }

// Same id as terraform.tfvars; the test fails if they drift.
const sheet = (id: string): NodeLink => ({
  kind: 'external',
  url: `https://docs.google.com/spreadsheets/d/${id}/edit`,
})
const PLACEMENT_SHEET = sheet('1GIdybswo8mzt1sBRq2AxZKetPr1jx2TPXvocY7S0EZw')
const FPA_WORKBOOK = sheet('1auHAu_VLRIFObKYYlbGtNBmc4zzDgTIhAhtLOpMPbOQ')
const EXPORT_SHEET = sheet('1G8NbbvEr6nFAFNxxV7JN6KXbYM0el1BVK4FBcaLBOaU')

const overview = (anchor: string): NodeLink => ({
  kind: 'section',
  target: { tab: 'overview', anchor },
})
const DATA_SOURCES_TAB: NodeLink = { kind: 'section', target: { tab: 'sources', anchor: null } }

const nodes: readonly MapNode[] = [
  {
    id: 'src-placement',
    column: 'source',
    title: 'Placement Sheet',
    detail: 'TS INPUT from Placement Sheet tab',
    link: PLACEMENT_SHEET,
  },
  {
    id: 'src-ar-plan',
    column: 'source',
    title: 'FP&A workbook',
    detail: '18 Month Net New AR Plan tab',
    link: FPA_WORKBOOK,
  },
  {
    id: 'src-payroll-tab',
    column: 'source',
    title: 'FP&A workbook',
    detail: 'ZK Payroll tab',
    link: FPA_WORKBOOK,
  },
  {
    // The payroll figure on the cash charts: GOSPEL row 4, 'Actual AP (Confirmed)'.
    id: 'src-gospel-tab',
    column: 'source',
    title: 'FP&A workbook',
    detail:
      PINNED_PAYROLL_SOURCE === null
        ? 'GOSPEL tab, Actual AP row (preview)'
        : 'GOSPEL tab, Actual AP row',
    link: FPA_WORKBOOK,
  },
  {
    id: 'src-funding',
    column: 'source',
    title: 'Funding rounds',
    detail: 'Recorded in the finance database',
    link: { kind: 'none' },
  },
  {
    id: 'src-headcount-editor',
    column: 'source',
    title: 'Headcount & AP plan',
    detail: 'Typed into this dashboard',
    link: overview('section-headcount-plan'),
  },
  {
    id: 'src-freee',
    column: 'source',
    title: 'freee accounting',
    detail: 'Costs and bank balance',
    link: { kind: 'pending' },
  },
  {
    id: 'src-freee-hr',
    column: 'source',
    title: 'freee HR',
    detail: 'Payslips',
    link: { kind: 'pending' },
  },

  {
    id: 'job-deal-ledger',
    column: 'job',
    title: 'Deal ledger sync',
    detail: '',
    cadence: 'Every 2 hours',
    job: 'deal-ledger',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-forecast',
    column: 'job',
    title: 'Forecast snapshot',
    detail: '',
    cadence: 'Daily',
    job: 'forecast-snapshot',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-cash-forecast-weekly',
    column: 'job',
    title: 'Weekly forecast copy',
    detail: 'Checks every night; saves that week\'s cash forecast on Mondays so it can be checked against what happened',
    cadence: 'Daily',
    job: 'cash-forecast-weekly',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-payroll',
    column: 'job',
    title: 'Payroll sync',
    detail: '',
    cadence: 'Monthly, on the 1st',
    job: 'payroll',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-gospel-ap',
    column: 'job',
    title: 'GOSPEL AP sync',
    detail: GOSPEL_ONLY
      ? 'Feeds the payroll figure on the cash charts'
      : PAYROLL_TAB_ONLY
        ? 'Feeds only Forecast vs actual'
        : 'Used when Payroll source is GOSPEL total',
    cadence: 'Monthly, on the 1st',
    job: 'gospel-ap',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-funding',
    column: 'job',
    title: 'Added by a developer',
    detail: '',
    cadence: 'When a round closes',
    link: { kind: 'none' },
  },
  {
    id: 'job-plan-publish',
    column: 'job',
    title: 'Published from the editor',
    detail: '',
    cadence: 'When someone publishes',
    link: { kind: 'none' },
  },
  {
    id: 'job-freee',
    column: 'job',
    title: 'freee sync',
    detail: '',
    cadence: 'Every 2 hours',
    job: 'freee',
    link: DATA_SOURCES_TAB,
  },
  {
    id: 'job-freee-hr',
    column: 'job',
    title: 'freee HR sync',
    detail: '',
    cadence: 'Monthly, on the 5th',
    // Not in sourceFreshness yet, so the box has no status line.
    job: 'freee-payroll',
    link: { kind: 'none' },
  },

  {
    id: 'sec-cash-in-out',
    column: 'section',
    title: 'Cash in and out',
    detail: 'Chart, headline figures, cash flow table',
    link: overview('section-cash-in-out'),
  },
  {
    id: 'sec-cash-position',
    column: 'section',
    title: 'Cash position',
    detail: 'Chart and scenario planner',
    link: overview('section-cash-position'),
  },
  {
    id: 'sec-forecast-vs-actual',
    column: 'section',
    title: 'Forecast vs actual',
    detail: 'A past week\'s forecast against what happened',
    link: overview('section-forecast-vs-actual'),
  },
  {
    id: 'sec-breakeven',
    column: 'section',
    title: 'Software breakeven',
    detail: '',
    link: overview('section-breakeven'),
  },
  {
    id: 'sec-overlap',
    column: 'section',
    title: 'Pipeline overlap',
    detail: '',
    link: overview('section-overlap'),
  },
  {
    id: 'sec-bank-reconciliation',
    column: 'section',
    title: 'Bank reconciliation',
    detail: 'Drift chart and table',
    link: overview('section-bank-reconciliation'),
  },
  {
    id: 'sec-headcount-plan',
    column: 'section',
    title: 'Headcount & AP plan',
    detail: 'Editor',
    link: overview('section-headcount-plan'),
  },
  {
    id: 'sec-plan-vs-actual',
    column: 'section',
    title: 'Plan vs actual payroll',
    detail: '',
    link: overview('section-plan-vs-actual'),
  },
  {
    id: 'sec-costs',
    column: 'section',
    title: 'Costs (freee) tab',
    detail: '',
    link: { kind: 'section', target: { tab: 'costs', anchor: null } },
  },
  {
    id: 'sec-export',
    column: 'section',
    title: 'Export sheet',
    detail: 'Clean figures for spreadsheet users',
    link: EXPORT_SHEET,
    output: true,
  },
]

// Each source feeds one job.
const intake: readonly MapEdge[] = [
  { from: 'src-placement', to: 'job-deal-ledger' },
  { from: 'src-ar-plan', to: 'job-forecast' },
  { from: 'src-payroll-tab', to: 'job-payroll' },
  { from: 'src-gospel-tab', to: 'job-gospel-ap' },
  { from: 'src-funding', to: 'job-funding' },
  { from: 'src-headcount-editor', to: 'job-plan-publish' },
  { from: 'src-freee', to: 'job-freee' },
  { from: 'src-freee-hr', to: 'job-freee-hr' },
]

// Which sections each job's data reaches.
const REACH: Record<string, readonly string[]> = {
  'job-deal-ledger': [
    'cash-in-out',
    'cash-position',
    'forecast-vs-actual',
    'breakeven',
    'overlap',
    'bank-reconciliation',
    'export',
  ],
  'job-forecast': [
    'cash-in-out',
    'cash-position',
    'forecast-vs-actual',
    'overlap',
    'bank-reconciliation',
    'export',
  ],
  // Reads the forecast already in the database, so it has no sheet of its own.
  // Its copies feed only the Forecast vs actual card.
  'job-cash-forecast-weekly': ['forecast-vs-actual'],
  'job-payroll': GOSPEL_ONLY
    ? ['breakeven', 'export']
    : ['cash-in-out', 'cash-position', 'breakeven', 'bank-reconciliation', 'export'],
  // The cash charts' payroll figure (with the switch back: only while it is on
  // GOSPEL total). Never the export. Forecast vs actual always reads the GOSPEL
  // series, whatever the payroll source, so it is reached either way.
  'job-gospel-ap': [
    ...(PAYROLL_TAB_ONLY ? [] : ['cash-in-out', 'cash-position', 'bank-reconciliation']),
    'forecast-vs-actual',
  ],
  'job-funding': [
    'cash-in-out',
    'cash-position',
    'forecast-vs-actual',
    'bank-reconciliation',
    'export',
  ],
  'job-plan-publish': [
    'cash-in-out',
    'cash-position',
    'forecast-vs-actual',
    'bank-reconciliation',
    'headcount-plan',
    'plan-vs-actual',
    'export',
  ],
  'job-freee': [
    'cash-position',
    'forecast-vs-actual',
    'bank-reconciliation',
    'plan-vs-actual',
    'costs',
    'export',
  ],
  'job-freee-hr': ['plan-vs-actual'],
}

const reach: readonly MapEdge[] = Object.entries(REACH).flatMap(([from, sections]) =>
  sections.map((section) => ({ from, to: `sec-${section}` })),
)

export const SOURCE_GRAPH: SourceGraph = { nodes, edges: [...intake, ...reach] }

// Every anchor a section target scrolls to; DashboardView must render each one.
export const SECTION_ANCHORS: readonly string[] = [
  ...new Set(
    SOURCE_GRAPH.nodes.flatMap((node) =>
      node.link.kind === 'section' && node.link.target.anchor !== null
        ? [node.link.target.anchor]
        : [],
    ),
  ),
]
